import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Download,
  PackageCheck,
  RefreshCw,
} from "lucide-react";
import { APIError, api, isPurchasePausedError, postJSON } from "../api";
import { useApp } from '../app-context';
import { PurchasePausePage } from '../components/PurchasePauseNotice';
import { PageLoading } from "../components/PageState";
import { SecureCardCheckout } from "../components/PaymentCheckout";
import { money } from "../format";
import { inboundProgress } from '../inbound-label-status';
import type {
  AddressValidation,
  Dashboard,
  ForwardingOrder,
  InboundLabelView,
  LabelReturnAddress,
  MoneyBreakdown,
} from "../types";

const blankOrigin: LabelReturnAddress = {
  name: "",
  company: "",
  line1: "",
  line2: "",
  city: "",
  subdivision: "",
  postalCode: "",
  countryCode: "US",
  phone: "",
  email: "",
  custom: false,
};
const states =
  "AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC".split(
    " ",
  );
const issue = (value: unknown) =>
  value instanceof Error
    ? value.message
    : "Could not complete this request. Please try again.";

export function InboundLabelEntry({ orderId }: { orderId: string }) {
  const { config } = useApp();
  const [view, setView] = useState<InboundLabelView | null>(null);
  useEffect(() => {
    let active = true;
    api<InboundLabelView>(`/api/orders/${orderId}/inbound-label`)
      .then((result) => {
        if (active) setView(result);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [orderId]);
  if (!view || (!view.shipment && (config.purchasesPaused || !(view.accessEnabled && view.eligible))))
    return null;
  return (
    <section className="inbound-entry">
      <div>
        <span className="mono-label">TO RX ROUTING</span>
        <h2>
          {view.shipment
            ? "Your inbound shipping label"
            : "Package at another US warehouse?"}
        </h2>
        <p>
          {view.shipment
            ? "View your separate label purchase, PDF, and inbound tracking."
            : "Buy a prepaid label for the trip to us. Your forwarding order covers the separate trip to your destination."}
        </p>
      </div>
      <Link
        className="button button--primary"
        to={`/orders/${orderId}/inbound-label`}
      >
        {view.shipment
          ? "View inbound label"
          : "Get a label to send your package to us"}
        <ArrowRight size={17} />
      </Link>
    </section>
  );
}

export function InboundLabel() {
  const { config } = useApp();
  const [serverPaused, setServerPaused] = useState(false);
  const { orderID = "" } = useParams();
  const endpoint = `/api/orders/${orderID}/inbound-label`;
  const [view, setView] = useState<InboundLabelView | null>(null);
  const [order, setOrder] = useState<ForwardingOrder | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [paymentBusy, setPaymentBusy] = useState(true);
  const [origin, setOrigin] = useState<LabelReturnAddress>(blankOrigin);
  const [reference, setReference] = useState("");
  const [measure, setMeasure] = useState({
    weight: "",
    length: "",
    width: "",
    height: "",
  });
  const [approved, setApproved] = useState(false);
  const [review, setReview] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState("");
  const [validation, setValidation] = useState<AddressValidation | null>(null);
  const load = useCallback(async () => {
    const result = await api<InboundLabelView>(endpoint);
    setView(result);
    if (result.shipment) {
      setSelected(result.shipment.selectedRateId || "");
      setOrigin(result.shipment.origin);
      setReference(result.shipment.warehouseReference);
      setMeasure({
        weight: String(result.shipment.parcel.weightGrams),
        length: String(result.shipment.parcel.lengthMm / 10),
        width: String(result.shipment.parcel.widthMm / 10),
        height: String(result.shipment.parcel.heightMm / 10),
      });
    }
    return result;
  }, [endpoint]);
  useEffect(() => {
    load().catch((value) => setError(issue(value)));
  }, [load]);
  useEffect(() => {
    let active = true;
    api<Dashboard>("/api/dashboard")
      .then((result) => {
        if (active)
          setOrder(result.orders.find((item) => item.id === orderID) || null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [orderID]);
  const needsPolling =
    !!view?.shipment &&
    (["pending", "unknown"].includes(view.shipment.paymentState) ||
      (view.shipment.paymentState === "succeeded" &&
        !view.shipment.labelAvailable &&
        !["failed", "refunded", "exception", "held"].includes(
          view.shipment.labelState,
        )));
  useEffect(() => {
    if (!needsPolling) return;
    const timer = window.setInterval(() => {
      void load().catch(() => {});
    }, 5000);
    return () => window.clearInterval(timer);
  }, [needsPolling, load]);
  const action = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try {
      await work();
    } catch (value) {
      if (isPurchasePausedError(value)) setServerPaused(true);
      else setError(issue(value));
    } finally {
      setBusy(false);
    }
  };
  const downloadLabel = () => action(async () => {
    const response = await fetch(`${endpoint}/label`, { credentials: 'include' });
    if (!response.ok) throw new Error('The label could not be downloaded. Check its status and try again.');
    const blob = await response.blob();
    if (!(await blob.slice(0, 5).text()).startsWith('%PDF-')) throw new Error('The label PDF is not ready yet. Check its status before downloading again.');
    const url = URL.createObjectURL(new Blob([blob], {type:'application/pdf'}));
    const link = document.createElement('a');
    link.href = url; link.download = `inbound-${view?.shipment?.rxid || orderID}.pdf`;
    document.body.appendChild(link); link.click(); link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
  const quote = async (acceptOriginal = false) => {
    setBusy(true);
    setError("");
    try {
      await postJSON(endpoint + "/quote", {
        origin,
        parcel: {
          weightGrams: Number(measure.weight),
          lengthMm: Math.round(Number(measure.length) * 10),
          widthMm: Math.round(Number(measure.width) * 10),
          heightMm: Math.round(Number(measure.height) * 10),
        },
        warehouseReference: reference,
        warehouseApproved: approved,
        acceptOriginal,
      });
      await load();
      setEditing(false);
      setReview(false);
      setValidation(null);
    } catch (value) {
      if (isPurchasePausedError(value)) setServerPaused(true);
      else if (
        value instanceof APIError &&
        value.data?.type === "address_review_required"
      )
        setValidation(value.data.validation as AddressValidation);
      else setError(issue(value));
    } finally {
      setBusy(false);
    }
  };
  if (!view)
    return (
      <div className="page">
        {error ? (
          <>
            <h1>Could not load your inbound label.</h1>
            <p role="alert">{error}</p>
            <button
              className="button button--outline"
              onClick={() => void action(load)}
            >
              Try again
            </button>
          </>
        ) : (
          <PageLoading />
        )}
      </div>
    );
  const shipment = view.shipment;
  const recovery =
    !!shipment && !["none", "failed", ""].includes(shipment.paymentState);
  if ((config.purchasesPaused || serverPaused) && !recovery) return <PurchasePausePage />;
  const canBuy = view.accessEnabled && view.eligible && view.purchasesEnabled;
  const rates = shipment?.rates || [];
  const chosen = rates.find((rate) => rate.id === shipment?.selectedRateId);
  const expired =
    !!shipment?.expiresAt &&
    new Date(shipment.expiresAt).getTime() <= Date.now();
  const status = shipment ? inboundProgress(shipment, !!order?.receivedAt) : null;
  const showForm =
    !recovery &&
    canBuy &&
    (!shipment || editing || rates.length === 0 || expired);
  const submitReview = (event: FormEvent) => {
    event.preventDefault();
    setReview(true);
    setValidation(null);
  };
  return (
    <div className="page page--inbound-label">
      <Link className="back-link" to={`/orders/${orderID}`}>
        <ArrowLeft size={16} />
        Back to forwarding order
      </Link>
      <header className="page-head">
        <div>
          <span className="mono-label">
            INBOUND SHIPPING LABEL
          </span>
          <h1>Get your package to us.</h1>
          <p>
            One package. Two separate trips. This label covers only the trip to
            RX Routing.
          </p>
          {order && <p className="mono-label inbound-order-context">{order.publicId} · RXID {order.rxid || shipment?.rxid}</p>}
        </div>
      </header>
      <ol className="inbound-route" aria-label="Your package journey">
        <li aria-current="step">
          <span className="inbound-route__number">1</span>
          <div>
            <b>To RX Routing</b>
            <span>
              {shipment?.origin.company || "Your US warehouse"} → RX Routing
            </span>
            <small>Separate inbound label purchase</small>
          </div>
        </li>
        <li>
          <span className="inbound-route__number">2</span>
          <div>
            <b>To your destination</b>
            <span>
              RX Routing →{" "}
              {order
                ? `${order.destination.city}, ${order.destination.countryCode}`
                : "your recipient"}
            </span>
            <small>
              {order?.authorizationMethod === "staff_bypass" ||
              order?.authorizationMethod === "email_entitlement"
                ? "Staff authorized forwarding order"
                : "Your existing forwarding order"}
              , after check-in
            </small>
          </div>
        </li>
      </ol>
      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}
      {expired && !recovery && canBuy && <p className="warning-box" role="status">Your previous rates have expired. Review the package details to get current prices before paying.</p>}
      {!view.accessEnabled && (
        <div className="warning-box" role="status">
          <b>New inbound-label purchases are not enabled.</b>
          <p>
            {shipment
              ? "You can still view an existing purchase and download its available label."
              : "Contact support if you need a label from an approved warehouse."}
          </p>
        </div>
      )}
      {view.accessEnabled && !view.eligible && !recovery && (
        <div className="warning-box" role="status">
          {view.unavailableReason ||
            "This order is not eligible for a new inbound label. It must be authorized and awaiting receipt."}
        </div>
      )}
      {!view.purchasesEnabled && !recovery && (
        <div className="warning-box" role="status">
          <b>Inbound label purchases are paused.</b>
          <p>
            No payment can be taken while label purchases are unavailable.
            Please check again later.
          </p>
        </div>
      )}
      {recovery && shipment && status ? (
        <div className="inbound-layout">
          <section className="inbound-panel inbound-result">
            <PackageCheck size={32} />
            <h2>{status.title}</h2>
            <p>{status.copy}</p>
            {shipment.message && <p role="status">{shipment.message}</p>}
            {shipment.labelAvailable &&
              shipment.paymentState !== "refunded" && (
                <>
                  <button
                    className="button button--primary"
                    disabled={busy}
                    onClick={()=>void downloadLabel()}
                  >
                    <Download size={17} />
                    Download label PDF
                  </button>
                  <p className="inbound-help">
                    Buying this label does not book a pickup or notify the
                    warehouse. Arrange handoff with them directly.
                  </p>
                </>
              )}
            {shipment.trackingCode && (
              <dl className="inbound-facts">
                <div>
                  <dt>Inbound carrier</dt>
                  <dd>
                    {shipment.carrier} · {shipment.service}
                  </dd>
                </div>
                <div>
                  <dt>Inbound tracking</dt>
                  <dd>{shipment.trackingCode}</dd>
                </div>
                <div>
                  <dt>Carrier status</dt>
                  <dd>{shipment.trackingStatus || "Awaiting carrier scan"}</dd>
                </div>
                <div>
                  <dt>Warehouse reference</dt>
                  <dd>{shipment.warehouseReference}</dd>
                </div>
              </dl>
            )}
            <button
              className="button button--outline"
              disabled={busy}
              onClick={() =>
                void action(async () => {
                  await postJSON(
                    endpoint +
                      (shipment.paymentState === "succeeded"
                        ? "/reconcile-label"
                        : "/payment/reconcile"),
                    {},
                  );
                  await load();
                })
              }
            >
              <RefreshCw size={16} />
              {busy ? "Checking…" : "Check purchase status"}
            </button>
          </section>
          <InboundPrice price={shipment.price} />
        </div>
      ) : (
        <>
          {showForm && (
            <div className="inbound-layout">
              <section className="inbound-panel">
                {!review ? (
                  <form onSubmit={submitReview}>
                    <h2>Where is your package now?</h2>
                    <p>
                      Use the dispatch address and packed measurements supplied
                      by the warehouse holding it, not your final delivery
                      address.
                    </p>
                    <fieldset disabled={busy}>
                      <legend>US dispatch address</legend>
                      <div className="form-grid">
                        {(
                          [
                            ["name", "Contact name"],
                            ["company", "Warehouse / company"],
                            ["line1", "Street address"],
                            ["line2", "Suite / unit (optional)"],
                            ["city", "City"],
                            ["subdivision", "State"],
                            ["postalCode", "ZIP code"],
                            ["phone", "Warehouse phone"],
                            ["email", "Shipping contact email (optional)"],
                          ] as const
                        ).map(([key, label]) => (
                          <label
                            key={key}
                            className={key === "line1" ? "span-2" : undefined}
                          >
                            {label}
                            {key === "subdivision" ? <select aria-label="State" required value={origin.subdivision} onChange={(event) => setOrigin({...origin, subdivision: event.target.value})}>
                              <option value="">Select state</option>
                              {states.map((state) => <option key={state}>{state}</option>)}
                            </select> : <input
                              required={!["line2", "email"].includes(key)}
                              type={
                                key === "email"
                                  ? "email"
                                  : key === "phone"
                                    ? "tel"
                                    : "text"
                              }
                              maxLength={key === "postalCode" ? 10 : 150}
                              aria-describedby={key === "email" ? "shipping-email-hint" : undefined}
                              pattern={key === 'postalCode' ? '[0-9]{5}(-[0-9]{4})?' : undefined}
                              value={origin[key]}
                              onChange={(event) =>
                                setOrigin({
                                  ...origin,
                                  [key]: event.target.value,
                                })
                              }
                            />}
                          </label>
                        ))}
                        <label>
                          Country
                          <input value="United States" readOnly />
                        </label>
                      </div>
                      <p className="card-checkout__hint" id="shipping-email-hint">
                        If the shipping contact email is blank, we use your account email for this shipment.
                      </p>
                    </fieldset>
                    <fieldset disabled={busy}>
                      <legend>Package details</legend>
                      <label>
                        Warehouse package reference
                        <input
                          required
                          maxLength={200}
                          value={reference}
                          onChange={(event) => setReference(event.target.value)}
                        />
                      </label>
                      <div className="inbound-measures">
                        {(
                          [
                            ["weight", "Packed weight (g)"],
                            ["length", "Length (cm)"],
                            ["width", "Width (cm)"],
                            ["height", "Height (cm)"],
                          ] as const
                        ).map(([key, label]) => (
                          <label key={key}>
                            {label}
                            <input
                              required
                              type="number"
                              min={key === "weight" ? 1 : 0.1}
                              max={key === "weight" ? 31751 : 152.4}
                              step={key === "weight" ? 1 : 0.1}
                              value={measure[key]}
                              onChange={(event) =>
                                setMeasure({
                                  ...measure,
                                  [key]: event.target.value,
                                })
                              }
                            />
                          </label>
                        ))}
                      </div>
                      <p className="inbound-help">
                        Include the outer packaging. Accurate measurements
                        help avoid carrier adjustments.
                      </p>
                      <label className="inbound-check">
                        <input
                          type="checkbox"
                          required
                          checked={approved}
                          onChange={(event) =>
                            setApproved(event.target.checked)
                          }
                        />
                        <span>
                          The warehouse has confirmed it accepts this prepaid
                          label and will send this package to RX Routing.
                        </span>
                      </label>
                      <p className="inbound-help">Send the PDF to your warehouse so they can print it, attach it to the package, and hand it to the carrier. This purchase does not book a pickup.</p>
                    </fieldset>
                    <button className="button button--primary" disabled={busy}>
                      Review package details
                      <ArrowRight size={16} />
                    </button>
                  </form>
                ) : (
                  <>
                    <h2>Review before getting rates.</h2>
                    <p>
                      The label will use this dispatch address. Keep the
                      warehouse’s package reference so they can identify the
                      right parcel.
                    </p>
                    <OriginReview origin={origin} />
                    <dl className="inbound-facts">
                      <div>
                        <dt>Warehouse reference</dt>
                        <dd>{reference}</dd>
                      </div>
                      <div>
                        <dt>Packed weight</dt>
                        <dd>{measure.weight} g</dd>
                      </div>
                      <div>
                        <dt>Outer dimensions</dt>
                        <dd>
                          {measure.length} × {measure.width} × {measure.height}{" "}
                          cm
                        </dd>
                      </div>
                      <div>
                        <dt>Destination</dt>
                        <dd>RX Routing · existing order RXID</dd>
                      </div>
                    </dl>
                    {validation && (
                      <section
                        className="warning-box"
                        aria-label="Address review"
                      >
                        <h3>Check the dispatch address.</h3>
                        <p>
                          {validation.messages?.join(" ") ||
                            "The address service needs your confirmation."}
                        </p>
                        {validation.recommended && (
                          <>
                            <p>
                              {validation.recommended.line1}{" "}
                              {validation.recommended.line2},{" "}
                              {validation.recommended.city},{" "}
                              {validation.recommended.subdivision}{" "}
                              {validation.recommended.postalCode}
                            </p>
                            <button
                              className="button button--outline"
                              onClick={() => {
                                setOrigin({
                                  ...origin,
                                  ...validation.recommended,
                                });
                                setValidation(null);
                              }}
                            >
                              Use suggested address
                            </button>
                          </>
                        )}
                      </section>
                    )}
                    <div className="inbound-actions">
                      <button
                        className="button button--outline"
                        disabled={busy}
                        onClick={() => setReview(false)}
                      >
                        Edit details
                      </button>
                      <button
                        className="button button--primary"
                        disabled={busy}
                        onClick={() => void quote(!!validation)}
                      >
                        {busy
                          ? "Getting rates…"
                          : validation
                            ? "Confirm entered address and get rates"
                            : "Get inbound rates"}
                      </button>
                    </div>
                  </>
                )}
              </section>
              <aside className="inbound-guidance">
                <h2>Before you buy</h2>
                <p>
                  Confirm the other warehouse accepts a prepaid label and can
                  transfer your package to us.
                </p>
                <p>
                  You will send them the PDF yourself. Postage does not include
                  pickup or their handling fees.
                </p>
                <hr />
                <b>Only the trip to RX Routing</b>
                <p>
                  Your existing forwarding order and its payment remain
                  separate. No second $5 handling fee is added here.
                </p>
                <small>
                  Available for one parcel dispatched from the 50 US states or
                  DC.
                </small>
              </aside>
            </div>
          )}
          {!showForm && canBuy && shipment && (
            <div className="inbound-layout">
              <section className="inbound-panel">
                <h2>
                  {chosen
                    ? "Review your inbound purchase."
                    : "Choose a service to RX Routing."}
                </h2>
                <p>
                  This total is for the US warehouse to RX Routing only.
                  Delivery times are carrier estimates.
                </p>
                {!chosen ? (
                  <>
                    <div className="rate-list">
                      {rates.map((rate) => (
                        <label
                          className={`rate-card ${selected === rate.id ? "rate-card--selected" : ""}`}
                          key={rate.id}
                        >
                          <input
                            type="radio"
                            name="inbound-rate"
                            checked={selected === rate.id}
                            onChange={() => setSelected(rate.id)}
                          />
                          <span className="carrier-mark">{rate.carrier}</span>
                          <div>
                            <b>{rate.service}</b>
                            <small>
                              {rate.deliveryDays
                                ? `${rate.deliveryDays} business days`
                                : "Carrier estimate at purchase"}
                            </small>
                          </div>
                          <em>
                            {money(rate.price.totalMinor, rate.price.currency)}
                          </em>
                          <i className="radio-dot" />
                        </label>
                      ))}
                    </div>
                    <div className="inbound-actions">
                      <button
                        className="button button--outline"
                        disabled={busy}
                        onClick={() => {
                          setEditing(true);
                          setApproved(true);
                        }}
                      >
                        Edit package
                      </button>
                      <button
                        className="button button--primary"
                        disabled={busy || !selected}
                        onClick={() =>
                          void action(async () => {
                            await postJSON(endpoint + "/select-rate", {
                              rateOptionId: selected,
                            });
                            await load();
                          })
                        }
                      >
                        {busy ? "Saving…" : "Review price and pay"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="inbound-checkout-review">
                      <b>
                        {chosen.carrier} · {chosen.service}
                      </b>
                      <OriginReview origin={shipment.origin} />
                      <p>
                        {shipment.parcel.weightGrams} g ·{" "}
                        {shipment.parcel.lengthMm / 10} ×{" "}
                        {shipment.parcel.widthMm / 10} ×{" "}
                        {shipment.parcel.heightMm / 10} cm
                      </p>
                      <p>
                        Warehouse reference:{" "}
                        <strong>{shipment.warehouseReference}</strong>
                      </p>
                    </div>
                    <SecureCardCheckout
                      key={shipment.id}
                      endpoint={endpoint + "/payment"}
                      rate={chosen}
                      description="Pay only for the label to RX Routing."
                      onPaid={() => {
                        void load().catch((value) => setError(issue(value)));
                      }}
                      onBusyChange={setPaymentBusy}
                    />
                    <button
                      className="button button--outline"
                      disabled={paymentBusy || busy}
                      onClick={() => {
                        setEditing(true);
                        setApproved(true);
                      }}
                    >
                      Change package or service
                    </button>
                  </>
                )}
              </section>
              <InboundPrice
                price={
                  shipment.price ||
                  rates.find((rate) => rate.id === selected)?.price
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
function OriginReview({ origin }: { origin: LabelReturnAddress }) {
  return (
    <address className="inbound-origin">
      <b>{origin.company}</b>
      <span>{origin.name}</span>
      <span>
        {origin.line1}
        {origin.line2 ? `, ${origin.line2}` : ""}
      </span>
      <span>
        {origin.city}, {origin.subdivision} {origin.postalCode} · US
      </span>
    </address>
  );
}
function InboundPrice({ price }: { price?: MoneyBreakdown }) {
  return (
    <aside className="price-summary inbound-price">
      <span className="mono-label">TO RX ROUTING ONLY</span>
      <h2>
        {price ? money(price.totalMinor, price.currency) : "Select a service"}
      </h2>
      {price && (
        <dl>
          <div>
            <dt>Inbound label</dt>
            <dd>{money(price.postageMinor, price.currency)}</dd>
          </div>
          <div>
            <dt>Payment processing</dt>
            <dd>{money(price.paymentFeeMinor, price.currency)}</dd>
          </div>
          <div>
            <dt>Inbound total</dt>
            <dd>{money(price.totalMinor, price.currency)}</dd>
          </div>
        </dl>
      )}
      <p>
        No additional $5 handling fee. The separate forwarding order covers the
        trip from RX Routing to your destination.
      </p>
    </aside>
  );
}
